import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  ActivityFileTypeEnum,
  ActivityTypeEnum,
  TimeslotStatusEnum,
  TimeslotWorkflowActionEnum,
  UpdateTimeslotBodyInterface,
} from '@sport-activities/sdk';
import { add, format } from 'date-fns';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { ActivityCardVariantEnum } from '~/component/activity-card/enum/variant.enum';
import { ActivityCardAttendeesInterface } from '~/component/activity-card/interfaces/attendees.interface';
import { VtmnButtonSizeEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-size.enum';
import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import { DividerVariantEnum } from '~/component/divider/enums/divider-variant.enum';
import { DatetimeVariantEnum } from '~/component/form/datetime/enums/variant.enum';
import { ModalEditTimeslotsModesEnum } from '~/component/modal-edit-timeslots/modal-edit-timeslots-modes.enum';
import { ModalEditTimeslotsComponent } from '~/component/modal-edit-timeslots/modal-edit-timeslots.component';
import { AlertModalComponent } from '~/component/modal/alert/alert.component';
import { ModalVariantEnum } from '~/component/modal/enums/modal-variant.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { SocialButtonVariantEnum } from '~/component/social-button/enums/variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { setDateTime } from '~/core/helpers/date/date.helper';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { RouterHistoryService } from '~/core/services/router-history/router-history.service';
import { TitleService } from '~/core/services/title/title.service';
import { dateFormatValidator } from '~/core/validators/date/date-format.validator';
import { timeFormatValidator } from '~/core/validators/date/time-format.validator';
import { LocaleService } from '~/i18n/services/locale.service';
import { OrganizerActivityStatusEnum } from '~/organizer/enums/activity-status.enum';
import { DisplayPlansModeEnum } from '~/organizer/shared/components/shared/display-plans/display-plans.component';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

import { environment } from '~environments/environment';

@Component({
  selector: 'app-organizer-timeslot-manage',
  templateUrl: './manage.component.html',
  styleUrls: ['./manage.component.scss'],
})
export class OrganizerTimeslotPageManageComponent implements OnInit, OnDestroy {
  @ViewChild('deleteTimeslotWarning')
  public deleteTimeslotWarningModal: AlertModalComponent;
  @ViewChild('deleteTimeslotModal')
  public modalDeleteTimeslots: ModalEditTimeslotsComponent;
  @ViewChild('shareModal')
  public shareModal: ModalComponent;

  private readonly frontBaseUrl = environment.applications.front.url;
  private readonly subscriptions = new Subscription();

  public readonly activityCardVariant = ActivityCardVariantEnum;
  public readonly ctaVariant = CtaVariantEnum;
  public readonly ctaWidth = CtaWidthEnum;
  public readonly datetimeVariant = DatetimeVariantEnum;
  public readonly displayPlansMode = DisplayPlansModeEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly modalVariant = ModalVariantEnum;
  public readonly socialButtonVariant = SocialButtonVariantEnum;
  public readonly timeslotDeletionModalMode =
    ModalEditTimeslotsModesEnum.DELETION;
  public readonly vtmnButtonSize = VtmnButtonSizeEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;

  public activity: Activity;
  public deleteAllOnConfirm = false;
  public isBirthday: boolean;
  public isConfirmModalButtonDisabled = false;
  public isLastTimeslot: boolean;
  public isMobileDevice: boolean;
  public isTabletDevice: boolean;
  public newTimeslotDateTime = new FormControl();
  public showDeletionSuccess = false;
  public showEditionSuccess = false;
  public successStatus = SuccessStatusEnum.LOADING;
  public timeslot: Timeslot;
  public upcomingTimeslots: Timeslot[];

  constructor(
    @Inject('Window') private readonly window: Window,
    @Inject(ACTIVITIES_BASE_URL) protected readonly activitiesUrl: string,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly httpClient: HttpClient,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly redirectionService: RedirectionService,
    private readonly titleService: TitleService,
    private readonly paramethorService: ParamethorService,
    private readonly routerHistory: RouterHistoryService,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly router: Router,
    private readonly localeService: LocaleService,
    private readonly matchMediaService: MatchMediaService,
    public readonly location: Location
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.organizer.timeslot.manage.title');

    const { timeslotData } = this.activatedRoute.snapshot.data;

    this.activity = timeslotData?.activity;
    this.timeslot = timeslotData?.timeslot;
    this.isBirthday =
      this.activity?.type === ('BIRTHDAY' as ActivityTypeEnum.BIRTHDAY) ||
      this.activity?.type === ('PRODUCT_TEST' as ActivityTypeEnum);

    this.newTimeslotDateTime.valueChanges.subscribe(({ date, time }) => {
      const isInvalidDate = new FormControl(date, dateFormatValidator())
        .invalid;
      const isInvalidTime = new FormControl(time, timeFormatValidator())
        .invalid;

      this.isConfirmModalButtonDisabled = isInvalidDate || isInvalidTime;
    });

    this.subscriptions.add(
      this.activityService
        .getAllActivityTimeslots(timeslotData?.activity.sku, {
          timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
          startDate: timeslotData?.timeslot.startDate,
        })
        .subscribe((upcomingTimeslots) => {
          this.upcomingTimeslots = upcomingTimeslots;
          this.isLastTimeslot = upcomingTimeslots?.length === 1;
        })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isTabletDevice().subscribe((isTabletDevice) => {
        this.isTabletDevice = isTabletDevice;
      })
    );
  }

  public get isActivityEvent(): boolean {
    return this.activity?.type === 'EVENT';
  }

  public get activityIsCanceled(): boolean {
    return (
      this.activity?.status.toLowerCase() ===
      OrganizerActivityStatusEnum.CANCELED
    );
  }

  public get activityUrl(): string {
    const path = getActivityDetailRouteName(
      this.activity?.type,
      this.activity?.sku
    );

    return (
      this.frontBaseUrl +
      this.localizeRouterService.translateRoute(path).toString()
    );
  }

  public get attendees(): ActivityCardAttendeesInterface {
    return {
      total: this.timeslot?.totalBooked,
      max: this.timeslot?.maximumAttendeeCapacity,
    };
  }

  public get backLink(): BackLinkInterface {
    return {
      path:
        this.routerHistory.previous ||
        this.localizeRouterService
          .translateRoute(
            `/organizer.activity.manage/${this.activity?.sku?.toString()}`
          )
          .toString(),
    };
  }

  public get deletionWarningModalTitle(): string {
    return this.deleteAllOnConfirm
      ? 'organizer.manage_timeslot.delete_modal.header.delete_all'
      : 'organizer.manage_timeslot.delete_modal.header.delete_single';
  }

  public formattedStartDate(date): string {
    return format(new Date(date), 'EEE dd MMM y', {
      locale: this.localeService.dateLocale,
    });
  }

  public formattedStartTime(date): string {
    if (this.isTimezoneSelectionEnabled && this.isLive) {
      return format(new Date(date), 'HH:mm (z)', {
        locale: this.localeService.dateLocale,
      });
    }

    const hourSet = this.setHours(
      new Date(this.timeslot?.startDate),
      this.timeslot?.startDateTimezone
    );

    return `${hourSet.getUTCHours().toString()}:${new Date(this.timeslot?.startDate).getUTCMinutes().toString().padStart(2, '0')}`;
  }

  public get hasGroupSizeSection(): boolean {
    return !this.isBirthday;
  }

  public get hasHeader(): boolean {
    return !this.isTabletDevice;
  }

  public get hasOffersSection(): boolean {
    return !this.isBirthday;
  }

  public get hasShareModal(): boolean {
    return !this.isMobileDevice || !this.window.navigator.share;
  }

  public get isDeletable(): boolean {
    return new Date(this.timeslot?.startDate) > new Date();
  }

  public get isLive(): boolean {
    return this.activity?.type === 'LIVE';
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.TimezoneSelection);
  }

  public get messengingLink(): string {
    return `/organizer.messaging.contact/${this.timeslot?.identifier}`;
  }

  public get participantLink(): string {
    return `/organizer.timeslot.participant.list/${this.timeslot?.identifier}`;
  }

  public get picture(): string {
    return (
      this.activity?.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.ICON
      ) || this.activity?.picture
    );
  }

  public get responsiveDividerVariant(): string {
    return this.isTabletDevice
      ? DividerVariantEnum.Large
      : DividerVariantEnum.Medium;
  }

  public get timeslotOfferMaxPrice(): number {
    return (
      this.timeslot?.offers?.length > 1 &&
      this.timeslot?.maxPriceOffer.price !==
        this.timeslot.minPriceOffer.price &&
      this.timeslot?.maxPriceOffer.price
    );
  }

  public closeDeleteTimeslotWarningModal(): void {
    this.deleteTimeslotWarningModal.hide();
  }

  public confirmDeleteAllTimeslot(): void {
    this.modalDeleteTimeslots.closeModal();
    this.deleteAllOnConfirm = true;
    if (
      this.upcomingTimeslots.some(
        (upcomingTimeslot) =>
          upcomingTimeslot.maximumAttendeeCapacity !==
          upcomingTimeslot.remainingAttendeeCapacity
      )
    ) {
      this.deleteTimeslotWarningModal.show();
    } else {
      this.deleteAllTimeslots();
    }
  }

  public confirmDeleteTimeslot(): void {
    this.modalDeleteTimeslots.closeModal();
    this.deleteAllOnConfirm = false;
    if (
      this.timeslot?.maximumAttendeeCapacity !==
      this.timeslot?.remainingAttendeeCapacity
    ) {
      this.deleteTimeslotWarningModal.show();
    } else {
      this.deleteTimeslot();
    }
  }

  public deleteAllTimeslots(): void {
    this.showDeletionSuccess = true;
    this.modalDeleteTimeslots.closeModal();

    this.activityService
      .updateTimeslotWorkflowBulk(
        this.activity?.sku,
        this.upcomingTimeslots.map(({ identifier }) => ({
          identifier,
          action: 'cancel' as TimeslotWorkflowActionEnum.CANCEL,
        }))
      )
      .pipe(
        mergeMap(() => {
          return this.activityService
            .getTimeslot(this.upcomingTimeslots?.[0]?.identifier)
            .pipe(
              repeatUntil(
                (timeslot) =>
                  timeslot.status ===
                  ('CANCELED' as TimeslotStatusEnum.CANCELED)
              )
            );
        })
      )
      .subscribe(() => {
        this.successStatus = SuccessStatusEnum.SUCCESS;
      });
  }

  public deleteSelectedTimeslots(): void {
    this.modalDeleteTimeslots.closeModal();
    const translatedPath = this.localizeRouter.translateRoute([
      '/organizer.timeslot.manage',
      this.timeslot?.identifier,
      'organizer.timeslot-edition.deletion',
      'organizer.timeslot-edition.select-timeslots',
    ]) as string[];

    this.router.navigate(translatedPath);
  }

  public deleteTimeslot(): void {
    this.showDeletionSuccess = true;
    this.subscriptions.add(
      this.activityService
        .postTimeslotAction(this.timeslot?.identifier, 'cancel')
        .pipe(
          mergeMap(() => {
            return this.activityService
              .getTimeslot(this.timeslot?.identifier)
              .pipe(
                repeatUntil(
                  (timeslot) =>
                    timeslot.status === ('CANCELED' as TimeslotStatusEnum)
                )
              );
          })
        )
        .subscribe(() => {
          this.successStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }

  public getActivityEditLink(id: number | string): string {
    return this.localizeRouterService
      .translateRoute(
        `/organizer.activity.manage/${id}/organizer.activity-edition.edition`
      )
      .toString();
  }

  public hideEditSuccess(): void {
    this.showEditionSuccess = false;
  }

  public onDeleteConfirm(): void {
    this.deleteTimeslotWarningModal.hide();
    if (this.deleteAllOnConfirm) {
      this.deleteAllTimeslots();
    } else {
      this.deleteTimeslot();
    }
  }

  public openShareModal(): void {
    if (!this.hasShareModal) {
      this.window.navigator.share({
        title: this.activity.name,
        url: this.activityUrl,
      });
    }
    this.shareModal.show();
  }

  public openTimeslotDeletionModal(): void {
    this.modalDeleteTimeslots.openModal();
  }

  public redirectAfterDeletion(): void {
    this.redirectionService.redirect('/organizer.planning.planning');
  }

  public setHours(date: Date, hours) {
    date.setUTCHours(date.getUTCHours() + hours);
    return date;
  }

  public updateTimeslotDate(): void {
    this.isConfirmModalButtonDisabled = false;
    this.showEditionSuccess = true;

    const startDate = setDateTime(
      this.newTimeslotDateTime?.value.date,
      this.newTimeslotDateTime?.value.time
    );
    const endDate = add(startDate, this.activity?.isoDuration);

    const timeslotBody: UpdateTimeslotBodyInterface = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    this.httpClient
      .patch<null>(
        `${this.activitiesUrl}/timeslots/${this.timeslot?.identifier}`,
        timeslotBody
      )
      .pipe(
        mergeMap(() => {
          return this.activityService
            .getTimeslot(this.timeslot?.identifier)
            .pipe(
              repeatUntil(
                (timeslot) =>
                  timeslot?.startDate.toString() === startDate.toISOString()
              )
            );
        })
      )
      .subscribe(() => {
        this.timeslot.startDate = startDate;
        this.timeslot.endDate = endDate;
        this.successStatus = SuccessStatusEnum.SUCCESS;
      });
  }
}
